import React, { useEffect, useState } from 'react';
import { useDatabase } from '../../../db/DatabaseProvider';
import { DollarSign, Activity, Package, Clock } from 'lucide-react';
import type { CustomerDocType } from '../../../db/schemas';
import { FormatCurrency } from '../../common/FormatCurrency';

interface Props {
  customer?: CustomerDocType | null;
}

export function CustomerTabSummary({ customer }: Props) {
  const targetCustomer = customer;
  if (!targetCustomer) return null;

  const db = useDatabase();
  const [stats, setStats] = useState({
    totalSpent: 0,
    sessionsCompleted: 0,
    activePackages: 0,
    lastVisit: null as string | null
  });

  useEffect(() => {
    if (!db || !targetCustomer?.id) return;
    
    let isMounted = true;
    const fetchStats = async () => {
      if (!targetCustomer?.id) return;
      // Get all tx
      const txs = await db.transactions.find({ selector: { customer_id: targetCustomer?.id || '', type: 'income', payment_status: 'paid', is_deleted: false } }).exec();
      const totalSpent = txs.reduce((acc, tx) => acc + tx.total_amount, 0);

      // Get all packages
      const paqs = await db.customer_packages.find({ selector: { customer_id: customer?.id || '', is_deleted: false } }).exec();
      const activePackages = paqs.filter(p => p.status === 'active' || p.status === 'is_active').length;
      
      let sessionsCompleted = 0;
      let lastVisit = customer?.updated_at || null; // Fallback
      
      paqs.forEach(p => {
        if (p.attentions) {
          sessionsCompleted += p.attentions.length;
          // find latest atencion
          p.attentions.forEach(a => {
            const atnDate = a.attention_date;
            if (atnDate && (!lastVisit || new Date(atnDate) > new Date(lastVisit))) {
              lastVisit = atnDate;
            }
          });
        }
      });

      if (isMounted) {
        setStats({ totalSpent, sessionsCompleted, activePackages, lastVisit });
      }
    };
    
    fetchStats();
    
    // Subscribe to changes
    const subTx = db.transactions.find({ selector: { customer_id: customer?.id || '' } }).$.subscribe(() => fetchStats());
    const subPaq = db.customer_packages.find({ selector: { customer_id: customer?.id || '' } }).$.subscribe(() => fetchStats());
    
    return () => {
      isMounted = false;
      subTx.unsubscribe();
      subPaq.unsubscribe();
    };
  }, [db, cliente?.id]);

  const cards = [
    { title: 'Total Gastado', value: <FormatCurrency amount={stats.totalSpent} />, icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { title: 'Sesiones Realizadas', value: stats.sessionsCompleted, icon: Activity, color: 'text-brand-sky', bg: 'bg-brand-sky/10' },
    { title: 'Paquetes Activos', value: stats.activePackages, icon: Package, color: 'text-brand-orange', bg: 'bg-brand-orange/10' },
    { title: 'Última Visita', value: stats.lastVisit ? new Date(stats.lastVisit).toLocaleDateString('es-ES') : 'N/A', icon: Clock, color: 'text-text-tertiary', bg: 'bg-surface-tertiary' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <h3 className="text-lg font-bold text-text-primary">Resumen del Cliente</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        {cards.map((card, idx) => (
          <div key={idx} className="bg-surface-secondary backdrop-blur-md border border-border rounded-xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md hover:border-brand-orange/30 transition-all cursor-default">
            <div className={`p-3 rounded-xl ${card.bg}`}>
              <card.icon className={`w-6 h-6 ${card.color}`} />
            </div>
            <div>
              <div className="text-xs font-semibold text-text-secondary uppercase tracking-wider">{card.title}</div>
              <div className="text-xl font-extrabold text-text-primary mt-0.5">{card.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
